/**
 * Layout Visibility Utility
 *
 * Centralizes complex header and sidebar visibility logic from App.tsx
 * Created: 2025-11-08
 * Updated: 2025-12-17 - Added sidebar visibility (security fix)
 * Issue: FE-008 - Simplify 18-line header conditional
 */

export interface LayoutVisibilityConfig {
  pathname: string;
  search: string;
  isAuthenticated: boolean;
}

// Alias for backwards compatibility
export type HeaderVisibilityConfig = LayoutVisibilityConfig;

// Routes where header should be hidden (these pages have their own headers)
const HIDE_HEADER_PATHS = [
  '/',                              // Homepage (clean fork-in-road design)
  '/fonctionnalites',               // Features page (has own header)
  '/features',                      // Features page (EN)
  '/tarifs',                        // Pricing page (has own header)
  '/pricing',                       // Pricing page (EN)
  '/notre-histoire',                // Entreprise page (has own header)
  '/blog',                          // Blog page (has own header)
  '/decouvrir',                     // Discovery wizard (independent flow)
  '/onboarding',                    // Onboarding flow (no distractions)
  '/auth/email-confirmation',       // Email confirmation (focus on action)
  '/reset-password',                // Password reset (focus on action, no distractions)
  '/auth/reset-password',           // Password reset redirect (focus on action)
  '/admin-v2',                      // Admin dashboard (has own header)
];

// Public pages where header should be shown (even if not authenticated)
const SHOW_HEADER_PUBLIC_PATHS = [
  '/help',
  '/privacy',
  '/terms',
  '/cookies',
  '/contact',
  '/simulateurs',
  '/plateforme',
  '/subventions/recherche',  // Subsidy search page (public access)
  '/subsidies/search',       // Alternative subsidy path
];

/**
 * Determines if the header should be displayed based on current route and auth state
 * 
 * Rules:
 * 1. Hide on specific paths (homepage, découvrir, onboarding, email confirmation)
 * 2. Hide on subsidy details when coming from découvrir flow
 * 3. Show on public info pages (help, privacy, etc.)
 * 4. Show for authenticated users (on all other routes)
 * 
 * @param config - Current route and authentication state
 * @returns true if header should be visible
 */
export function shouldShowHeader(config: HeaderVisibilityConfig): boolean {
  const { pathname, search, isAuthenticated } = config;

  // Rule 1: Hide header on specific paths
  // Special case: homepage requires exact match (not startsWith to avoid matching all paths)
  if (pathname === '/') {
    return false;
  }
  // Other paths can use startsWith
  if (HIDE_HEADER_PATHS.slice(1).some(path => pathname.startsWith(path))) {
    return false;
  }

  // Rule 2: Hide subsidies page when coming from découvrir flow
  if (pathname.startsWith('/subsidies') && search.includes('from=decouvrir')) {
    return false;
  }

  // Rule 3: Show on public info pages
  if (SHOW_HEADER_PUBLIC_PATHS.some(path => pathname.startsWith(path))) {
    return true;
  }

  // Rule 4: Show if authenticated (on all other routes)
  return isAuthenticated;
}

/**
 * Determines if auth buttons should be shown in header
 *
 * @param pathname - Current route path
 * @param isAuthenticated - Whether user is logged in
 * @returns true if auth buttons should be visible
 */
export function shouldShowAuthButtons(pathname: string, isAuthenticated: boolean): boolean {
  // Don't show auth buttons on auth page or when user is logged in
  return !pathname.startsWith('/auth') && !isAuthenticated;
}

/**
 * SECURITY: Paths where sidebar should NEVER appear
 *
 * These are authentication flows, onboarding, and public pages where
 * showing the sidebar would be a security vulnerability or UX issue.
 * Even if a user has a partial session (e.g., from clicking a magic link),
 * the sidebar should NOT render on these paths.
 */
const HIDE_SIDEBAR_PATHS = [
  // Auth flows - NEVER show sidebar
  '/auth',                    // All auth pages (login, register, callback, confirm, MFA)
  '/reset-password',          // Password reset
  '/accept-invite',           // Invite acceptance

  // Admin dashboard - has its own layout
  '/admin-v2',

  // Onboarding - clean focused experience
  '/onboarding',

  // Discovery flow (though redirects to /)
  '/decouvrir',

  // Public marketing pages - clean design
  '/',                        // Homepage
  '/tarifs',                  // Pricing
  '/pricing',                 // Pricing (EN)
  '/fonctionnalites',         // Features
  '/features',                // Features (EN)
  '/notre-histoire',          // About/Company
  '/documentation',           // Docs
  '/entreprise',              // Enterprise
  '/ressources',              // Resources
  '/product-details',         // Product details
  '/landing-v2',              // Landing variations
  '/landing-old',

  // Public info pages
  '/help',
  '/privacy',
  '/terms',
  '/cookies',
  '/contact',

  // Public product pages
  '/simulateurs',
  '/plateforme',
  '/entreprises',
  '/consultants',
  '/particuliers',

  // Public SEO pages
  '/regions',
  '/guides',

  // NOTE: /subventions/recherche is NOT hidden - authenticated users should see sidebar
  // Unauthenticated users won't see sidebar anyway (Rule 2: isAuthenticated check)

  // Payment callbacks
  '/success',
  '/cancel',

  // Other public pages
  '/preferences/unsubscribe',
  '/health',
  '/qa-test',
  '/blog',                  // Blog page (public)
];

/**
 * Determines if the sidebar should be displayed
 *
 * SECURITY CRITICAL: This function ensures the sidebar NEVER appears on
 * auth pages or public pages, even if the user has a partial session.
 *
 * Rules:
 * 1. NEVER show on paths in HIDE_SIDEBAR_PATHS (security + UX)
 * 2. ONLY show for authenticated users on protected routes
 *
 * @param config - Current route and authentication state
 * @returns true if sidebar should be visible
 */
export function shouldShowSidebar(config: LayoutVisibilityConfig): boolean {
  const { pathname, isAuthenticated } = config;

  // Rule 1: Never show sidebar on hidden paths
  // Check exact match for root path
  if (pathname === '/') {
    return false;
  }

  // Check if path starts with any hidden path
  // Use startsWith for paths that have sub-routes
  for (const hidePath of HIDE_SIDEBAR_PATHS) {
    if (hidePath === '/') continue; // Already handled above

    // Exact match or starts with path + /
    if (pathname === hidePath || pathname.startsWith(hidePath + '/')) {
      return false;
    }

    // Also match paths with query params (e.g., /auth?mode=login)
    // pathname doesn't include query params, so this just catches
    // cases where the route exactly matches
    if (pathname.startsWith(hidePath)) {
      return false;
    }
  }

  // Rule 2: Only show for authenticated users
  return isAuthenticated;
}
